package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Text

internal val vertriebPflichtHeader = listOf(
    "ID", "ICP", "Lead Typ", "Zustand", "Firma", "Straße", "Hausnummer", "PLZ", "Ort",
    "Google Maps", "Webseite", "Ansprechpartner", "Position", "E-Mail", "LinkedIn", "Telefon", "Mobil", "Leadquelle", "Bereich", "Zuständig"
)


private val vertriebPflichtHeaderIds = listOf(
    "lead_id", "icp", "lead_typ", "zustand", "firma", "strasse", "hausnummer", "plz", "ort",
    "google_maps", "webseite", "ansprechpartner", "position", "email", "linkedin", "telefon", "mobil", "lead_quelle", "vertriebBereich", "akquiseZugewiesenAn"
)

internal fun vertriebTabularHeaders(headers: List<VertriebsindexApi.ZusatzHeader>) = vertriebPflichtHeaderIds + headers.map { it.id }
internal fun vertriebTabularLabels(headers: List<VertriebsindexApi.ZusatzHeader>) = vertriebPflichtHeader + headers.map { it.label }
internal fun vertriebFixedLeadingHeaders() = vertriebPflichtHeaderIds

internal fun vertriebRow(lead: All, headers: List<VertriebsindexApi.ZusatzHeader>): List<String> = listOf(
    lead.id, lead.icp, lead.leadTyp,
    when (lead.vertriebBereich) {
        "INDEX" -> if (lead.status == "Storniert") "Storniert" else "Angelegt"
        "POOL" -> "Vorbereitet"
        else -> checkpointLabel(lead.vertriebCheckpoint.ifBlank { "MY_LEADS" })
    }, lead.firma,
    lead.strasse, lead.hausnummer, lead.plz, lead.ort, vertriebGoogleMapsUrl(lead),
    lead.webseite, lead.ansprechpartner, lead.ansprechpartnerPosition, lead.email,
    lead.linkedin, lead.telefon, lead.mobil, lead.leadQuelle.ifBlank { if (lead.akquiseTyp == "LinkedIn") "LinkedIn" else "Web" },
    when (lead.vertriebBereich) { "POOL" -> "Vertriebspool"; "MINE" -> "Meine Leads"; else -> "Vertriebsindex" },
    lead.akquiseZugewiesenAn.ifBlank { lead.akquiseZugewiesenAnId }
) + headers.map { header ->
    val value = lead.vertriebZusatzfelder[header.id].orEmpty()
    if (header.typ == "CHECKBOX") {
        if (value.equals("true", true) || value == "1" || value.equals("ja", true) || value == "✅") "✅" else "⬜"
    } else value
}

internal fun headerButton(enabled: Boolean, onClick: () -> Unit) = TabularToolbarButton(
    label = "Header Verwaltung", icon = "images/einstellungen.svg", enabled = enabled, onClick = onClick
)

@Composable
internal fun HeaderHinzufuegenDialog(
    windowKey: String,
    headers: List<VertriebsindexApi.ZusatzHeader>,
    onClose: () -> Unit,
    onChanged: (List<VertriebsindexApi.ZusatzHeader>) -> Unit
) {
    var entries by remember { mutableStateOf(listOf(VertriebsindexApi.NeuerHeader("", ""))) }
    var editedHeaders by remember(headers) { mutableStateOf(headers) }
    var error by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var deleteTarget by remember { mutableStateOf<VertriebsindexApi.ZusatzHeader?>(null) }
    var deleteError by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    val typeOptions = remember { listOf(
        All(key = "TEXT", label = "Text"),
        All(key = "CHECKBOX", label = "Checkbox"),
        All(key = "LINK", label = "Link")
    ) }
    PopupEngineCard(
        windowKey = windowKey, zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 700, heightPx = 700, minWidthPx = 480, minHeightPx = 520,
        centered = true, viewportAnchored = true, resizable = true, allowContentOverflow = true,
        onClose = { if (!busy) onClose() }
    ) {
        Div({
            attr("role", "dialog")
            attr("aria-modal", "true")
            attr("aria-labelledby", "$windowKey-title")
            style {
                padding(28.px)
                width(100.percent)
                height(100.percent)
                boxSizing("border-box")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                color(DashboardTheme.TEXT_LIGHT)
            }
        }) {
            Div({
                id("$windowKey-title")
                style { textAlign("center"); fontSize(20.px); fontWeight("700") }
            }) { Text("Header Verwaltung") }
            Div({ style { flex("1"); minHeight(0.px); overflowY("auto"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); justifyContent(JustifyContent.FlexStart) } }) {
                Div({ style { width(600.px); maxWidth(100.percent); property("flex-shrink", "0"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(12.px) } }) {
                    Div({ style { textAlign("center"); fontSize(16.px); fontWeight("600") } }) { Text("Meine Header") }
                    if (headers.isEmpty()) Div({ style { textAlign("center") } }) { Text("Noch keine Zusatzheader angelegt.") }
                    headers.forEach { header ->
                        val edited = editedHeaders.firstOrNull { it.id == header.id } ?: header
                        Div({ style { width(100.percent); display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.FlexEnd) } }) {
                            Div({ style { flex("2"); minWidth(190.px) } }) {
                                Input(InputType.Text, attrs = {
                                    value(edited.label)
                                    attr("placeholder", "Label")
                                    attr("maxlength", "60")
                                    attr("aria-label", "Label für ${header.label}")
                                    if (busy) attr("disabled", "true")
                                    onInput { event -> editedHeaders = editedHeaders.map {
                                        if (it.id == header.id) it.copy(label = event.value) else it
                                    } }
                                    style {
                                        width(100.percent); minWidth(0.px); height(40.px)
                                        padding(0.px, 12.px); boxSizing("border-box"); borderRadius(8.px)
                                        backgroundColor(Color("rgba(23, 26, 32, 0.38)"))
                                        property("border", "1px solid #303645")
                                        color(DashboardTheme.TEXT_LIGHT); fontSize(14.px)
                                    }
                                })
                            }
                            Div({ style { flex("1"); minWidth(150.px) } }) {
                                AppDropdown(
                                    options = typeOptions,
                                    selectedValue = edited.typ,
                                    onChange = { selected -> editedHeaders = editedHeaders.map {
                                        if (it.id == header.id) it.copy(typ = selected) else it
                                    } },
                                    placeholder = "Header Typ",
                                    enabled = !busy,
                                    appearance = AppDropdownAppearance.Surface
                                )
                            }
                            Img(src = "images/miniclose.svg", attrs = {
                                attr("role", "button"); attr("tabindex", "0"); attr("aria-label", "Header ${header.label} löschen")
                                onClick { if (!busy) { deleteTarget = header; deleteError = "" } }
                                style { width(20.px); height(20.px); property("cursor", if (busy) "default" else "pointer"); property("flex-shrink", "0") }
                            })
                        }
                    }
                    if (editedHeaders != headers) LumaticaButton("Änderungen speichern", {
                        if (!busy && editedHeaders.all { it.label.isNotBlank() }) {
                            busy = true
                            scope.launch {
                                try {
                                    onChanged(VertriebsindexApi.updateHeaders(editedHeaders.map { it.copy(label = it.label.trim()) }))
                                } catch (e: CancellationException) { throw e }
                                catch (e: Exception) { error = e.message ?: "Header konnten nicht aktualisiert werden." }
                                finally { busy = false }
                            }
                        }
                    }, variant = LumaticaButtonVariant.Action,
                        enabled = editedHeaders.all { it.label.isNotBlank() } && deleteTarget == null && !busy)

                    Div({ style {
                        width(100.percent); marginTop(8.px); marginBottom(2.px)
                        property("border-top", "1px solid #303645")
                    } })
                    Div({ style { textAlign("center"); fontSize(16.px); fontWeight("600") } }) { Text("Neue Header hinzufügen") }
                    entries.forEachIndexed { index, entry ->
                            Div({ style { width(100.percent); display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.FlexEnd) } }) {
                                Div({ style { flex("2"); minWidth(190.px) } }) {
                                    Input(InputType.Text, attrs = {
                                        value(entry.label)
                                        attr("placeholder", "Label")
                                        attr("maxlength", "60")
                                        attr("aria-label", "Label für neuen Header ${index + 1}")
                                        if (busy) attr("disabled", "true")
                                        onInput { event -> entries = entries.mapIndexed { position, value ->
                                            if (position == index) value.copy(label = event.value) else value
                                        } }
                                        style {
                                            width(100.percent); minWidth(0.px); height(40.px)
                                            padding(0.px, 12.px); boxSizing("border-box"); borderRadius(8.px)
                                            backgroundColor(Color("rgba(23, 26, 32, 0.38)"))
                                            property("border", "1px solid #303645")
                                            color(DashboardTheme.TEXT_LIGHT); fontSize(14.px)
                                        }
                                    })
                                }
                                Div({ style { flex("1"); minWidth(150.px) } }) {
                                    AppDropdown(
                                        options = typeOptions,
                                        selectedValue = entry.typ.ifBlank { null },
                                        onChange = { selected -> entries = entries.mapIndexed { position, value ->
                                            if (position == index) value.copy(typ = selected) else value
                                        } },
                                        placeholder = "Header Typ",
                                        enabled = !busy,
                                        appearance = AppDropdownAppearance.Surface
                                    )
                                }
                                if (entries.size > 1) Img(src = "images/miniclose.svg", attrs = {
                                    attr("role", "button"); attr("tabindex", "0"); attr("aria-label", "Diese neue Header-Zeile entfernen")
                                    onClick { if (!busy) entries = entries.filterIndexed { position, _ -> position != index } }
                                    style { width(20.px); height(20.px); property("cursor", if (busy) "default" else "pointer"); property("flex-shrink", "0") }
                                })
                            }
                    }
                    LumaticaButton("+ Weiterer Header", {
                        entries = entries + VertriebsindexApi.NeuerHeader("", "")
                        error = ""
                    }, variant = LumaticaButtonVariant.State, enabled = !busy)
                    if (error.isNotBlank()) Div({ attr("role", "alert"); style { color(Color("#ffb4b4")); textAlign("center") } }) { Text(error) }
                    LumaticaButton("Header hinzufügen", {
                        if (entries.all { it.label.isNotBlank() && it.typ.isNotBlank() } && !busy) {
                            busy = true
                            scope.launch {
                                try {
                                    onChanged(VertriebsindexApi.addHeaders(entries.map { it.copy(label = it.label.trim()) }))
                                    entries = listOf(VertriebsindexApi.NeuerHeader("", ""))
                                } catch (e: CancellationException) { throw e }
                                catch (e: Exception) { error = e.message ?: "Header konnte nicht gespeichert werden." }
                                finally { busy = false }
                            }
                        }
                    }, variant = LumaticaButtonVariant.Action,
                        enabled = entries.all { it.label.isNotBlank() && it.typ.isNotBlank() } && !busy)
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(12.px); justifyContent(JustifyContent.Center) } }) {
                LumaticaButton("Abbrechen", onClose, variant = LumaticaButtonVariant.State, enabled = !busy)
            }
        }
    }
    val target = deleteTarget
    if (target != null) PopupEngineCard(
        windowKey = "$windowKey-delete-${target.id}",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX + 1,
        widthPx = 520, heightPx = 330, minWidthPx = 420, minHeightPx = 280,
        centered = true, viewportAnchored = true, resizable = false,
        onClose = { if (!busy) { deleteTarget = null; deleteError = "" } }
    ) {
        Div({
            attr("role", "alertdialog"); attr("aria-modal", "true")
            attr("aria-labelledby", "$windowKey-delete-title")
            style {
                width(100.percent); height(100.percent); padding(30.px); boxSizing("border-box")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(18.px)
                color(DashboardTheme.TEXT_LIGHT); textAlign("center")
            }
        }) {
            Div({ id("$windowKey-delete-title"); style { fontSize(19.px); fontWeight("700") } }) { Text("Header löschen?") }
            Div { Text("Soll „${target.label}“ wirklich gelöscht werden?") }
            if (deleteError.isNotBlank()) Div({ attr("role", "alert"); style { color(Color("#ffb4b4")); fontSize(14.px) } }) { Text(deleteError) }
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); gap(12.px); paddingTop(4.px) } }) {
                LumaticaButton("Abbrechen", { if (!busy) { deleteTarget = null; deleteError = "" } },
                    variant = LumaticaButtonVariant.State, enabled = !busy)
                LumaticaButton("Löschen", {
                    if (!busy) {
                        busy = true
                        deleteError = ""
                        scope.launch {
                            try {
                                onChanged(VertriebsindexApi.deleteHeader(target.id))
                                deleteTarget = null
                            } catch (e: CancellationException) { throw e }
                            catch (e: Exception) {
                                val marker = Regex("vertrieb_header_daten_vorhanden:(\\d+)").find(e.message.orEmpty())
                                deleteError = if (marker != null) {
                                    "Löschen nicht möglich: In ${marker.groupValues[1]} Leads sind noch Werte für diesen Header gespeichert."
                                } else e.message ?: "Header konnte nicht gelöscht werden."
                            } finally { busy = false }
                        }
                    }
                }, variant = LumaticaButtonVariant.Action, enabled = !busy)
            }
        }
    }
}
